import { CheckCircle2, XCircle } from "lucide-react";
import { t as statusText } from "@/lib/i18n";
import { StatusBadge } from "@/components/status-badge";
import { cn } from "@/lib/utils";
import { ThresholdBar } from "@/components/evaluation/doneness-gauge";
import type { ObstacleCheck } from "@/lib/obstacles";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

type BlurDetails = {
  p_blurred?: number | null;
  threshold?: number | null;
  enforced?: boolean;
  would_fail?: boolean;
  skipped?: string | null;
};

/** the percentage formatter of the UI language ("12.5%" / "12,5 %") */
function usePercent() {
  const { formatNumber } = useLocalization();
  return (value: number) =>
    formatNumber(value, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Blur CNN output: probability bar vs threshold, shadow-mode verdict, or why the check was skipped. */
function BlurCheckDetails({ details }: { details?: BlurDetails }) {
  const { t } = useLocalization();
  if (!details) {
    return null;
  }
  if (details.skipped) {
    return (
      <span className="text-xs text-muted-foreground">
        {t("detail.photo.skipped", { reason: details.skipped })}
      </span>
    );
  }
  if (details.p_blurred == null || details.threshold == null) {
    return null;
  }
  const blurred = details.p_blurred >= details.threshold;
  const shadowFail = details.enforced === false && details.would_fail;
  return (
    <>
      <ThresholdBar
        probability={details.p_blurred}
        threshold={details.threshold}
        barClassName={
          !blurred ? "bg-muted-foreground/45" : shadowFail ? "bg-amber-500" : "bg-red-500"
        }
      />
      {shadowFail && (
        <span className="text-xs text-amber-400">{t("detail.photo.blurShadow")}</span>
      )}
    </>
  );
}

/**
 * Obstacle check as a chart: the share of the pizza the segmentation found covered against its threshold, and
 * the crust gap against its own when that is what failed (the only signal of evaluations made before the
 * segmentation). `preview` is the on-demand result for such an evaluation: drawn the same way and labelled,
 * the stored status above it stays as it is.
 */
function ObstacleCheckDetails({ check, preview }: { check: ObstacleCheck; preview?: ObstacleCheck | null }) {
  const { t } = useLocalization();
  const percent = usePercent();
  const className = (name: string) =>
    ["hand", "paper", "lid"].includes(name) ? t(`obstacle.class.${name}` as TranslationKey) : name;
  const shown = preview ?? check;
  const details = shown.details;
  if (!details) {
    return null;
  }
  const segmentation = details.segmentation;
  const found = (segmentation?.classes ?? [])
    .filter((c) => c.coverage > 0)
    .map((c) => `${className(c.name)} ${percent(c.coverage)}`)
    .join(", ");
  const hasGap = details.max_gap_ratio != null && details.threshold_ratio != null;
  // evaluations made before the segmentation do not name the failing signal: there it can only be the gap
  const gapFailed = details.detected_by ? details.detected_by.includes("crust_gap") : shown.status === "NOK";
  return (
    <>
      {segmentation?.skipped && (
        <span className="text-xs text-muted-foreground">
          {t("detail.photo.segmentationSkipped", { reason: segmentation.skipped })}
        </span>
      )}
      {segmentation && segmentation.coverage != null && (
        <>
          <ThresholdBar
            probability={segmentation.coverage}
            threshold={segmentation.threshold}
            max={Math.max(0.2, segmentation.threshold * 3, segmentation.coverage * 1.15)}
            barClassName={
              !segmentation.would_fail
                ? "bg-muted-foreground/45"
                : segmentation.enforced
                  ? "bg-red-500"
                  : "bg-amber-500"
            }
          />
          {found && (
            <span className="text-xs text-muted-foreground">{t("detail.photo.coveredBy", { list: found })}</span>
          )}
          {!segmentation.enforced && segmentation.would_fail && (
            <span className="text-xs text-amber-400">{t("detail.photo.segmentationShadow")}</span>
          )}
        </>
      )}
      {hasGap && gapFailed && (
        <>
          <span className="text-xs text-muted-foreground">
            {t("detail.photo.crustGap", { value: percent(details.max_gap_ratio!) })}
          </span>
          <ThresholdBar
            probability={details.max_gap_ratio!}
            threshold={details.threshold_ratio!}
            max={Math.max(details.threshold_ratio! * 2, details.max_gap_ratio! * 1.15, 0.1)}
            barClassName="bg-red-500"
          />
        </>
      )}
      {hasGap && !gapFailed && (
        <span className="text-xs text-muted-foreground">
          {t("detail.photo.crustGapLimit", {
            value: percent(details.max_gap_ratio!),
            limit: percent(details.threshold_ratio!),
          })}
        </span>
      )}
      {preview && (
        <span className="text-xs text-amber-400">
          {preview.status !== check.status
            ? t("detail.photo.previewWouldBe", { status: statusText(preview.status) })
            : t("detail.photo.preview")}
        </span>
      )}
    </>
  );
}

/** names of the backend's photo-quality checks; others are shown by their key */
const CHECK_LABELS: Record<string, TranslationKey> = {
  pizza_blur: "detail.photo.check.pizza_blur",
  obstacle_in_pizza: "detail.photo.check.obstacle_in_pizza",
  pizza_visibility: "detail.photo.check.pizza_visibility",
};

/**
 * The photo-quality checks as compact rows (icon, name, value, verdict) with their charts: the blur
 * probability and the covered share against their thresholds, the crust gap when that is what failed.
 */
export function PhotoQualityChecks({
  metadata,
  obstaclePreview,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: any;
  /** on-demand obstacle check of an evaluation made before the obstacle segmentation (not stored) */
  obstaclePreview?: ObstacleCheck | null;
}) {
  const { t } = useLocalization();
  const percent = usePercent();
  const photoQuality = metadata?.photo_quality_evaluation;
  if (!photoQuality?.checks) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {Object.entries(photoQuality.checks).map(([key, check]: [string, any]) => {
        const checkOk = check.status === "OK";
        const value =
          key === "pizza_blur" && check.details?.p_blurred != null
            ? percent(check.details.p_blurred)
            : key === "obstacle_in_pizza" &&
                (obstaclePreview ?? check).details?.segmentation?.coverage != null
              ? percent((obstaclePreview ?? check).details.segmentation.coverage)
              : null;
        return (
          <li key={key} className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm">
              {checkOk ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-400/80" />
              ) : (
                <XCircle className="h-3.5 w-3.5 shrink-0 text-red-400" />
              )}
              <span className={cn("min-w-0 flex-1 truncate", checkOk ? "text-foreground/80" : "font-medium")}>
                {CHECK_LABELS[key] ? t(CHECK_LABELS[key]) : key.replace(/_/g, " ")}
              </span>
              {value && (
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    checkOk ? "text-muted-foreground" : "font-semibold text-red-300",
                  )}
                >
                  {value}
                </span>
              )}
              {!checkOk && <StatusBadge status={check.status} label={statusText(check.status)} size="xs" />}
            </div>
            <div className="flex flex-col gap-1 pl-[1.375rem]">
              {!checkOk && check.reason && (
                <span className="text-xs text-red-300/90">{statusText(check.reason)}</span>
              )}
              {key === "pizza_blur" && <BlurCheckDetails details={check.details} />}
              {key === "obstacle_in_pizza" && (
                <ObstacleCheckDetails check={check} preview={obstaclePreview} />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
